import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocusealForm } from '@docuseal/react';
import { api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import type { SigningDocumentSummary } from '../../../lib/types';
import styles from './Hub.module.css';

const DOCUSEAL_HOST = 'https://docuseal.com';

export function DocumentsView() {
  const { data: me } = useCreatorMe();
  const qc = useQueryClient();
  const docsQuery = useQuery({
    queryKey: ['creator', 'documents'],
    queryFn: () => api.get<SigningDocumentSummary[]>('/api/creator/documents'),
    enabled: !!me,
  });

  if (!me) return null;

  const docs = docsQuery.data ?? [];
  const allSigned = docs.length > 0 && docs.every((d) => d.status === 'completed');

  const headerBadge = allSigned ? (
    <span className={`${styles.badge} ${styles.badgeGreen}`}>All signed</span>
  ) : docs.length === 0 ? (
    <span className={`${styles.badge} ${styles.badgeGray}`}>Being prepared</span>
  ) : (
    <span className={`${styles.badge} ${styles.badgeOrange}`}>Awaiting signature</span>
  );

  return (
    <>
      <div className={styles.pageActionRow}>
        <div>
          <div className={styles.pageTitle}>Documents</div>
          <div className={styles.pageSubtitle}>
            Legal documents for your Kick Health partnership.
          </div>
        </div>
        {headerBadge}
      </div>

      {docsQuery.isPending && (
        <div className={styles.statusRow}>
          <span className={styles.statusRowLabel}>Loading your documents…</span>
        </div>
      )}

      {docsQuery.isSuccess && docs.length === 0 && (
        <div className={styles.statusRow}>
          <span className={styles.statusRowLabel}>
            We're preparing your documents and will notify you at {me.email} when they're ready.
          </span>
        </div>
      )}

      {docs.map((doc) => (
        <DocumentCard
          key={doc.id}
          doc={doc}
          email={me.email}
          onComplete={() => qc.invalidateQueries({ queryKey: ['creator', 'documents'] })}
        />
      ))}
    </>
  );
}

function DocumentCard({
  doc,
  email,
  onComplete,
}: {
  doc: SigningDocumentSummary;
  email: string;
  onComplete: () => void;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <div className={styles.cardSectionTitle}>📄 {doc.template_name}</div>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>
          {doc.status === 'completed' ? 'Signed' : doc.status}
        </div>
      </div>
      {doc.description && (
        <div className={styles.statusRow}>
          <span className={styles.statusRowLabel}>{doc.description}</span>
        </div>
      )}

      {doc.status === 'completed' && (
        <div className={styles.contractCta}>
          <div className={styles.signedConfirmation}>
            <div className={styles.signedCheck}>✓</div>
            <div>
              <div className={styles.signedLabel}>Document signed</div>
              <div className={styles.signedDate}>
                Signed on {doc.completed_at ? formatDate(doc.completed_at) : '—'}
              </div>
            </div>
          </div>
          {doc.signed_pdf_url && (
            <a
              href={doc.signed_pdf_url}
              target="_blank"
              rel="noreferrer"
              className={styles.btnPrimary}
              style={{ marginTop: 12, display: 'inline-block' }}
            >
              View signed PDF
            </a>
          )}
        </div>
      )}

      {doc.status === 'pending' && doc.submitter_slug && (
        // DocuSeal's web component fills 100% of its container — without an
        // explicit height the iframe collapses to 0. 820px shows roughly one
        // PDF page; the embed handles its own internal scroll past that.
        <div style={{ minHeight: 820, display: 'flex' }}>
          <DocusealForm
            src={`${DOCUSEAL_HOST}/s/${doc.submitter_slug}`}
            email={email}
            withDownloadButton={false}
            onComplete={onComplete}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {(doc.status === 'declined' || doc.status === 'expired') && (
        <div className={styles.statusRow}>
          <span className={styles.statusRowLabel}>
            This document was {doc.status}. Reach out to the team and we'll re-send it.
          </span>
        </div>
      )}
    </div>
  );
}
